// import components
import { Icon } from '@sdb/planning-ui';
import { TASK_LIST_WIDTH } from 'components/shared/TaskList';
import {
  ActionButton,
  ActionButtonsContainer,
  TimelineBarContainer,
  TimelineContainer,
  TimelineGrayBackgroundBox,
  ZoomButtonsContainer
} from './TimelineBar.styles';
import { TimeWindow } from './TimeWindow';

// import types
import { TimelineBarProps } from './TimelineBar.types';

// import utils
import { differenceInSeconds } from 'date-fns';
import {
  alignScrollPosition,
  getItemWidthInPixels,
  getNextZoomLevel,
  getPreviousZoomLevel,
  hasNextZoomLevel,
  hasPreviousZoomLevel
} from 'components/roster-portal/activity-planning/TimelineView/utils';

export const TimelineBar = (props: TimelineBarProps) => {
  const {
    innerRef,
    interval,
    zoomLevel,
    timeBarItemWidth,
    nextDisabled,
    previousDisabled,
    toPlanDrawerIsOpen,
    isTaskListOpen,
    scrollContainerRef,
    onChangeZoomLevel,
    onChangeCurrentDay,
    onNext,
    onPrevious
  } = props;
  const scrollContainer = scrollContainerRef?.current;
  const containerWidth = scrollContainer?.clientWidth ?? 0;
  const hasScrollbar =
    containerWidth < timeBarItemWidth * (interval?.timeWindow?.length ?? 0);

  return (
    <TimelineBarContainer>
      <TimelineGrayBackgroundBox />
      <ZoomButtonsContainer>
        <ActionButton
          onClick={() => {
            const previousZoomLevel = zoomLevel;
            const newZoomLevel = getPreviousZoomLevel(zoomLevel);
            onChangeZoomLevel?.(newZoomLevel);
            alignScrollPosition(
              scrollContainer,
              newZoomLevel,
              previousZoomLevel
            );
          }}
          disabled={!hasPreviousZoomLevel(zoomLevel) || !hasScrollbar}
        >
          <Icon type="magnifying-glass-minus" fontSize="inherit" />
        </ActionButton>
        <ActionButton
          onClick={() => {
            const previousZoomLevel = zoomLevel;
            const newZoomLevel = getNextZoomLevel(zoomLevel);
            onChangeZoomLevel?.(newZoomLevel);
            alignScrollPosition(
              scrollContainer,
              newZoomLevel,
              previousZoomLevel
            );
          }}
          disabled={!hasNextZoomLevel(zoomLevel)}
        >
          <Icon type="magnifying-glass-plus" fontSize="inherit" />
        </ActionButton>
      </ZoomButtonsContainer>

      <ActionButtonsContainer isTaskListOpen={isTaskListOpen}>
        <ActionButton
          onClick={() => onPrevious?.()}
          disabled={previousDisabled}
        >
          <Icon type="chevron-left" fontSize="inherit" />
        </ActionButton>

        <ActionButton onClick={() => onNext?.()} disabled={nextDisabled}>
          <Icon type="chevron-right" fontSize="inherit" />
        </ActionButton>
      </ActionButtonsContainer>

      <TimelineContainer
        ref={innerRef}
        sx={
          toPlanDrawerIsOpen
            ? {
                '&:after': {
                  right: `${TASK_LIST_WIDTH + 14}px`
                }
              }
            : {}
        }
      >
        {interval?.timeWindow?.map((time, i) => {
          const previousTime = interval?.timeWindow?.[i - 1];
          const nextTime = interval?.timeWindow?.[i + 1];
          const durationInSeconds = differenceInSeconds(time, interval?.start);
          const positionX = getItemWidthInPixels(
            timeBarItemWidth,
            durationInSeconds,
            zoomLevel
          );

          return (
            <TimeWindow
              key={`${time}-${i}`}
              time={time}
              previousTime={previousTime}
              nextTime={nextTime}
              x={positionX}
              width={timeBarItemWidth}
              toPlanDrawerIsOpen={toPlanDrawerIsOpen}
              zoomLevel={zoomLevel}
              dateOffset={i == 0 ? positionX : 0}
              onChangeCurrentDay={onChangeCurrentDay}
            />
          );
        })}
      </TimelineContainer>
    </TimelineBarContainer>
  );
};
